<template>
  <div :class="{
    'flight-item-wrapper': true,
    'is-active': selectedFlight === 0 || selectedFlight === flightData.id
  }">
    <div
      class="flight-item-real-time"
      :style="getFlightRealPosition()">
    </div>
    <div
      :class="{
        'flight-item': true,
        'is-confirmed': flightData.is_confirmed,
        'has-passengers': flightData.passengers_count > 0 || flightData.tbc_count > 0,
        'is-narrow': isNarrow,
        'is-too-small-for-labels': isTooSmallForLabels
      }"
      @click="showFlightDetails(flightData.id)"
      :style="getFlightPositionCss">
      <template v-if="alwaysShowIcao || zoomLevel === '1' || zoomLevel === '2'">
        <div :class="{
          'flight-item-departure-airport': true,
          'text-primary': flightData.departure_airport_notes
        }">
          {{ flightData.departure_airport_icao }}
        </div>
        <div :class="{
          'flight-item-arrival-airport': true,
          'text-primary': flightData.arrival_airport_notes
        }">
          {{ flightData.arrival_airport_icao }}
        </div>
        <div class="flight-item-departure-hour">
          {{ flightData.departure_date | displayHour }}
        </div>
        <div class="flight-item-arrival-hour">
          {{ flightData.arrival_date | displayHour }}
        </div>
      </template>

      <span
        class="warnings-indicator"
        :data-warning-mode="getWarningMode"
        :title="flightData.warnings_unconfirmed_count + ' warnings'"
        @click.stop="$bus.$emit('show-flight-warnings-popup', flightData)"></span>
      <span
        :class="{
          'checklist-indicator': true,
          'is-completed': flightData.check_lists_count === flightData.check_lists_checked_count,
          'is-not-completed': flightData.check_lists_count !== flightData.check_lists_checked_count
        }"
        :title="flightData.check_lists_checked_count + ' / ' + flightData.check_lists_count"></span>
    </div>
    <div
      class="flight-item-tooltip"
      :style="getTooltipPositionCss">
      <strong class="flight-item-tooltip-aircraft">
        {{ aircraftReginMark }} / {{ aircraftCallSign }}
      </strong>

      <div class="flight-item-tooltip-date">
        <span class="flight-item-tooltip-date-departure">
          {{ flightData.departure_date | displayDate }}
        </span>

        <span
          v-if="flightData.departure_date.split(' ')[0] !== flightData.arrival_date.split(' ')[0]"
          class="flight-item-tooltip-date-arrival">
        - {{ flightData.arrival_date | displayDate }}
        </span>
      </div>

      <div class="flight-item-tooltip-departure-info">
        <span class="flight-item-tooltip-departure-label">DEP</span>
        <span class="flight-item-tooltip-departure-airport">
          {{ flightData.departure_airport_icao }}/{{ flightData.departure_airport_name }}
        </span>
        <span class="flight-item-tooltip-departure-time">
          {{ flightData.departure_date | displayHour }}&nbsp;Z‏‏‎ ‎&nbsp;<br>
          {{ flightData.departure_local_date | displayHourWithDay }}&nbsp;LT
        </span>
        <span
          v-if="flightData.departure_handler"
          class="flight-item-tooltip-departure-handler">
          {{ flightData.departure_handler.name }} <small v-if="flightData.departure_handler.phone">- {{ flightData.departure_handler.phone }}</small>
        </span>
      </div>

      <div class="flight-item-tooltip-arrival-info">
        <span class="flight-item-tooltip-arrival-label">ARR</span>
        <span class="flight-item-tooltip-arrival-airport">
          {{ flightData.arrival_airport_icao }}/{{ flightData.arrival_airport_name }}
        </span>
        <span class="flight-item-tooltip-arrival-time">
          {{ flightData.arrival_date | displayHour }}&nbsp;Z‏‏‎ ‎&nbsp;<br>
          {{ flightData.arrival_local_date | displayHourWithDay }}&nbsp;LT
        </span>
        <span
          v-if="flightData.arrival_handler"
          class="flight-item-tooltip-arrival-handler">
          {{ flightData.arrival_handler.name }} <small v-if="flightData.arrival_handler.phone">- {{ flightData.arrival_handler.phone }}</small>
        </span>
      </div>

      <div class="flight-item-tooltip-crew-pax-info">
        <span class="flight-item-tooltip-crew">
          Crew: {{ flightData.crew_count }}
        </span>

        <span class="flight-item-tooltip-pax">
          Pax: {{ passengersDivisionText }} <template v-if="flightData.tbc_count > 0">(includes {{ flightData.tbc_count }} TBC)</template>
        </span>
      </div>

      <div
        v-if="flightData.arrival_airport_notes || flightData.departure_airport_notes"
        class="flight-item-tooltip-airport-notes">
        <div v-if="flightData.departure_airport_notes">
          <strong>
            Departure airport notes:
          </strong>
          <pre>
            {{ flightData.departure_airport_notes }}
          </pre>
        </div>

        <div v-if="flightData.arrival_airport_notes">
          <strong>
            Arrival airport notes:
          </strong>
          <pre>
            {{ flightData.arrival_airport_notes }}
          </pre>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DateUtils from './../../utils/DateUtils.js';

export default {
  name: 'calendar-flight-item',
  props: [
    'alwaysShowIcao',
    'aircraftReginMark',
    'aircraftCallSign',
    'dateFrom',
    'dateTo',
    'flightData',
    'selectedFlight',
    'zoomLevel'
  ],
  filters: {
    displayHour (value) {
      if (value) {
        value = value.split(' ')[1];
        value = value.split(':');
        value = [value[0], value[1]].join('');
        return value;
      }

      return '----';
    },
    displayHourWithDay (value) {
      if (value) {
        let parts = value.split(' ');
        value = parts[1];
        value = value.split(':');
        value = [value[0], value[1]].join('');
        return parts[0].split('-')[2] + ' ' + value;
      }

      return '----';
    },
    displayDate (value) {
      if (value) {
        let input = new Date(value);

        return [
          input.getDate(),
          DateUtils.getMonthShortName(input.getMonth() + 1),
          input.getFullYear().toString().substr(2, 2)
        ].map(n => n < 10 ? '0' + n : n).join('');
      }

      return '';
    }
  },
  computed: {
    percentageTooltipHalfWidth () {
      return ((400 / document.querySelector('.calendar-content').offsetWidth) / 2) * 100.0;
    },
    isNarrow () {
      let position = this.getFlightPosition;

      if (100 - position.left - position.right < 2) {
        return true;
      }

      return false;
    },
    isTooSmallForLabels () {
      let position = this.getFlightPosition;

      if (100 - position.left - position.right <= 6) {
        return true;
      }

      return false;
    },
    getFlightPositionCss () {
      let position = this.getFlightPosition;
      return 'left: ' + position.left + '%;right: ' + position.right + '%;';
    },
    getTooltipPositionCss () {
      let position = this.getFlightPosition;
      let left = parseFloat(position.left + (position.width / 2));
      let noTransform = '';
      let right = '';

      if (left < this.percentageTooltipHalfWidth) {
        left = '0%';
        noTransform = 'transform: none!important;';
      } else if (100 - left < this.percentageTooltipHalfWidth) {
        left = 'auto';
        right = 'right: 0!important;';
        noTransform = 'transform: none!important;';
      } else {
        left += '%';
      }

      return 'left: ' + left + ';' + noTransform + right;
    },
    getFlightPosition () {
      let validFrom = new Date(this.flightData.departure_date);
      let validTo = new Date(this.flightData.arrival_date);
      let rangeFrom = new Date(this.dateFrom);
      let rangeTo = new Date(this.dateTo);
      let left = 0;
      let right = 0;

      if (this.flightData.valid_to === null) {
        validTo = rangeTo;
      }

      if (validFrom > rangeFrom) {
        let diff = validFrom - rangeFrom;
        left = (diff / (rangeTo - rangeFrom)) * 100;
      }

      if (validTo < rangeTo) {
        let diff = rangeTo - validTo;
        right = (diff / (rangeTo - rangeFrom)) * 100;
      }

      return {
        left,
        right,
        width: 100 - left - right
      };
    },
    getWarningMode () {
      if (this.flightData.warnings_count === 0) {
        return 'level-no-warning';
      }

      if (this.flightData.warnings_unconfirmed_count === 0) {
        return 'level-green';
      }

      if (this.flightData.warnings_unconfirmed_count > 0) {
        return 'level-red';
      }

      return 'level-unknown';
    },
    passengersDivisionText () {
      let output = [];

      ['M', 'F', 'C', 'i'].forEach(item => {
        if (this.flightData.passengers_division[item]) {
          output.push(this.flightData.passengers_division[item] + item);
        }
      });

      if (output.length) {
        return output.join(' ');
      }

      return '--';
    }
  },
  methods: {
    getFlightRealPosition () {
      let validFrom = new Date(this.flightData.departure_date);
      let validTo = new Date(this.flightData.arrival_date);

      if (this.flightData.real_departure_date) {
        validFrom = new Date(this.flightData.real_departure_date);
      }

      if (this.flightData.real_arrival_date) {
        validTo = new Date(this.flightData.real_arrival_date);
      } else if (this.flightData.real_departure_date && !this.flightData.real_arrival_date) {
        let diff = validTo - validFrom;
        validTo = new Date(validTo + diff);
      }

      let rangeFrom = new Date(this.dateFrom);
      let rangeTo = new Date(this.dateTo);
      let left = 0;
      let right = 0;

      if (this.flightData.valid_to === null) {
        validTo = rangeTo;
      }

      if (validFrom > rangeFrom) {
        let diff = validFrom - rangeFrom;
        left = (diff / (rangeTo - rangeFrom)) * 100;
      }

      if (validTo < rangeTo) {
        let diff = rangeTo - validTo;
        right = (diff / (rangeTo - rangeFrom)) * 100;
      }

      return 'left: ' + left + '%; right: ' + right + '%;';
    },
    showFlightDetails (flightID) {
      this.$bus.$emit('flight-details-display', flightID);
    }
  }
}
</script>

<style lang="scss" scoped>
.flight-item {
  background: var(--info);
  bottom: 25px;
  color: #fff;
  cursor: pointer;
  font-size: 12px;
  height: 50px;
  line-height: 50px;
  padding: 0;
  position: absolute;
  text-align: center;
  top: 25px;

  &:before {
    background: repeating-linear-gradient(45deg,rgba(0,0,0,0),rgba(0,0,0,0) 10px, rgba(255, 255, 255, .75) 0, rgba(255, 255, 255, .75) 20px);
    content: "";
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
  }

  &-wrapper {
    opacity: .5;
    transition: opacity .25s ease-out;

    &.is-active {
      opacity: 1;
    }
  }

  &.is-confirmed {
    &:before {
      display: none;
    }
  }

  &.has-passengers {
    background: var(--primary);
  }

  &-departure-airport,
  &-arrival-airport,
  &-departure-hour,
  &-arrival-hour {
    color: #333;
    font-size: 12px;
    height: 20px;
    line-height: 20px;
    position: absolute;
    text-shadow: 0 0 1px #fff, 0 0 2px #fff, 0 0 3px #fff, 0 0 1px #fff, 0 0 2px #fff, 0 0 3px #fff, 0 0 1px #fff, 0 0 2px #fff, 0 0 3px #fff;
    z-index: 1;
  }

  &-departure-airport,
  &-arrival-airport {
    font-size: 10px;
  }

  &-departure-airport {
    left: 0;
    top: -20px;
  }

  &-arrival-airport {
    right: 0;
    top: -20px;
  }

  &-departure-hour {
    bottom: -20px;
    font-size: 10px;
    left: 0;
  }

  &-arrival-hour {
    bottom: -20px;
    font-size: 10px;
    right: 0;
  }

  &.is-too-small-for-labels {
    .flight-item {
      &-departure-airport {
        left: -85px;
        text-align: right;
        width: 80px;
      }

      &-arrival-airport {
        right: -85px;
        text-align: left;
        width: 80px;
      }

      &-departure-hour {
        left: -85px;
        text-align: right;
        width: 80px;
      }

      &-arrival-hour {
        right: -85px;
        text-align: left;
        width: 80px;
      }
    }
  }

  .warnings-indicator {
    border-radius: 8px;
    border: 1px solid #fff;
    cursor: help;
    height: 15px;
    position: absolute;
    right: 5px;
    top: 5px;
    width: 15px;
    z-index: 2;

    &[data-warning-mode="level-amber"] {
      background: var(--warning);
    }

    &[data-warning-mode="level-red"] {
      background: var(--danger);
    }

    &[data-warning-mode="level-green-light"] {
      background: var(--success);
      filter: brightness(150%);
    }

    &[data-warning-mode="level-no-warning"] {
      background: #1ac571;
    }

    &[data-warning-mode="level-green"] {
      background: #bfff00;
    }

    &[data-warning-mode="level-unknown"] {
      background: var(--light);
    }
  }

  .checklist-indicator {
    bottom: 5px;
    border-radius: 8px;
    border: 1px solid #fff;
    cursor: help;
    height: 15px;
    position: absolute;
    right: 5px;
    width: 15px;
    z-index: 2;

    &.is-not-completed {
      background: var(--danger);
    }

    &.is-completed {
      background: var(--success);
    }
  }

  &.is-narrow {
    .warnings-indicator,
    .checklist-indicator {
      left: 50%;
      right: auto;
      transform: translateX(-50%);
    }
  }

  &-real-time {
    background: var(--danger);
    cursor: pointer;
    height: 10px;
    position: absolute;
    top: 15px;
    z-index: 1;
  }

  &-tooltip {
    background: var(--secondary);
    border: 1px solid var(--light);
    border-radius: 4px;
    box-shadow: 0 5px 3px rgba(0, 0, 0, .125);
    color: var(--light);
    display: none;
    font-weight: 300;
    padding: 10px 15px;
    pointer-events: none;
    position: absolute;
    right: auto!important;
    top: calc(100% - 10px);
    transform: translateX(-50%);
    width: 400px;
    z-index: 100;

    &-aircraft {
      display: block;
      font-size: 16px;
      font-weight: bold;
    }

    &-arrival-info,
    &-departure-info,
    &-crew-pax-info,
    &-airport-notes {
      align-items: flex-start;
      border-top: 1px solid var(--light);
      display: flex;
      flex-wrap: wrap;
      margin-top: 10px;
      padding-top: 10px;
    }

    &-airport-notes {
      strong {
        font-size: 12px;
        font-weight: 900;
      }

      pre {
        color: #fff;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
        white-space: pre-line;
        word-break: break-word;
      }
    }

    &-crew {
      width: 80px;
    }

    &-pax {
      width: 180px;
    }

    &-arrival,
    &-departure {
      &-label {
        display: block;
        width: 48px;
      }

      &-airport {
        display: block;
        font-size: 12px;
        line-height: 24px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        width: 220px;
      }

      &-time {
        display: block;
        font-family: monospace;
        padding-left: 5px;
        text-align: right;
        width: 100px;
      }

      &-handler {
        display: block;
        font-size: 12px;
        margin-top: -18px;
        width: 300px;
      }
    }
  }

  &:hover {
    & + .flight-item-tooltip {
      display: block;
    }
  }
}
</style>
